<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'

import InputDateSelector from '@/components/shared/InputDateSelector.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'

const props = defineProps({
  invoice: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()

const projects = props.invoice.projects || []

const editItem = ref({
  invoice: props.invoice.id,
  document_row_type: webSiteStore.documentRowTypeEnum.COMMENT,
  quantity: 0,
  price: 0,
  project: projects.length === 1 ? projects[0].id : null,
  date: null,
  description: '',
})
const { isUpdated, isUpdating } = useIsUpdated(editItem)

const form = ref(null)

const onCancel = () => emit('cancel')
useNavigator(onCancel)
useEscapeClose(isUpdated, onCancel)

const onSave = async () => {
  isUpdating.value = true
  try {
    const { data } = await accountingStore.upsertInvoiceRowAsync(editItem.value)
    notifySuccess(i18n.t('info.createdSuccessfully'))
    emit('update', data.id)
  } finally {
    isUpdating.value = false
  }
}
</script>

<template>
  <q-card class="dialog-card-flex">
    <navigate-back
      :text="`${i18n.t('common.add')} ${i18n.t('common.comment').toLocaleLowerCase()}`"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll dialog-card-content">
      <q-form ref="form" lazy-rules="ondemand">
        <q-card-section class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-file-document-outline"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">{{ i18n.t('common.basicInfo') }}</span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-select
              v-model="editItem.project"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.project')"
              :options="projects"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              clearable
            />
            <input-date-selector
              v-model="editItem.date"
              clearable
              :range="false"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.date')"
            />
            <q-input
              v-model="editItem.description"
              class="col-12"
              :label="i18n.t('common.comment')"
              autogrow
              autofocus
              :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
              lazy-rules="ondemand"
            />
          </div>
        </q-card-section>
      </q-form>
    </q-card-section>
    <q-card-actions class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.add')"
        :loading="isUpdating"
        @click="form.validate().then(success => success && onSave())"
      />
    </q-card-actions>
  </q-card>
</template>
